import React from "react";

import { Modal } from "reactstrap";

import {
  PUJA_DASHAMI_RITUALS,
  PUJA_TIMETABLE,
  PUJA_TIMETABLE_BN,
  PUJA_TIMETABLE_BN_NOTE,
  PUJA_TIMETABLE_BN_TITLE,
  PUJA_YEAR,
} from "utils/pujaSchedule";
import { prepareTimetableFile, shareTimetable } from "utils/shareTimetable";

import "assets/css/flow.css";

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.38-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35zM12.04 21.5h-.01a9.4 9.4 0 0 1-4.79-1.31l-.34-.2-3.56.93.95-3.47-.22-.36a9.38 9.38 0 0 1-1.44-5.01c0-5.18 4.22-9.4 9.41-9.4 2.51 0 4.87.98 6.65 2.76a9.34 9.34 0 0 1 2.75 6.65c0 5.19-4.22 9.41-9.4 9.41zM20.5 3.49A11.32 11.32 0 0 0 12.04 0C5.79 0 .71 5.08.71 11.32c0 1.99.52 3.94 1.51 5.65L.61 24l7.18-1.88a11.3 11.3 0 0 0 5.4 1.38h.01c6.24 0 11.32-5.08 11.32-11.32 0-3.03-1.18-5.87-3.32-8.01z" />
    </svg>
  );
}

function TimetableModal({ isOpen, onClose }) {
  const [sharing, setSharing] = React.useState(false);
  const [shareError, setShareError] = React.useState(null);
  const fileRef = React.useRef(null);

  // Render the poster as soon as the modal opens, so the share sheet can be
  // opened straight off the tap while the user gesture is still valid.
  React.useEffect(() => {
    if (!isOpen || fileRef.current != null) {
      return;
    }
    let cancelled = false;
    prepareTimetableFile()
      .then((file) => {
        if (!cancelled) {
          fileRef.current = file;
        }
      })
      .catch(() => {
        /* the tap will retry and surface any error then */
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleShare = async () => {
    setSharing(true);
    setShareError(null);
    try {
      await shareTimetable(fileRef.current);
    } catch (e) {
      setShareError("Could not prepare the image. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      centered
      scrollable
      contentClassName="hm-modal"
      toggle={onClose}
    >
      <div className="hm-modal-head">
        <div>
          <span className="hm-modal-eyebrow">Durga Puja {PUJA_YEAR}</span>
          <h5 className="hm-modal-title">Date &amp; time table</h5>
        </div>
        <button aria-label="Close" className="hm-modal-x" type="button" onClick={onClose}>
          <span aria-hidden={true}>&times;</span>
        </button>
      </div>
      <div className="hm-modal-body">
        <div className="hm-tt">
          <div className="hm-tt-main">
            <div className="hm-tt-scroll">
              <table className="hm-tt-table">
                <thead>
                  <tr>
                    <th>Tithi</th>
                    <th>Date</th>
                    <th>Upto</th>
                  </tr>
                </thead>
                <tbody>
                  {PUJA_TIMETABLE.map((row, i) => (
                    <tr key={`${row.tithi}-${row.date}-${i}`}>
                      <th>{row.tithi}</th>
                      <td>
                        <span className="hm-tt-date">{row.date}</span>
                        <span className="hm-tt-day">{row.day}</span>
                      </td>
                      <td className="hm-tt-time">
                        {row.time != null ? (
                          <>
                            <span className="hm-tt-upto">Upto</span>
                            {row.time}
                          </>
                        ) : (
                          <span className="hm-tt-none" aria-label="No end time published">
                            &ndash;
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="hm-tt-rituals">
              <span className="hm-tt-rituals-label">On Dashami</span>
              {PUJA_DASHAMI_RITUALS}
            </p>
          </div>

          <aside className="hm-tt-bn">
            <h6 className="hm-tt-bn-title">{PUJA_TIMETABLE_BN_TITLE}</h6>
            <ul className="hm-tt-bn-list">
              {PUJA_TIMETABLE_BN.map((row) => (
                <li key={row.tithi}>
                  <b>{row.tithi}</b>
                  <span>
                    {row.date} &middot; {row.day}
                  </span>
                </li>
              ))}
            </ul>
            <p className="hm-tt-bn-note">
              <b>{PUJA_TIMETABLE_BN_NOTE.label}</b>
              {PUJA_TIMETABLE_BN_NOTE.text}
            </p>
          </aside>
        </div>
      </div>
      <div className="hm-modal-foot hm-tt-foot">
        {shareError != null && <p className="hm-tt-share-err">{shareError}</p>}
        <button
          className="hm-btn hm-btn-wa"
          type="button"
          onClick={handleShare}
          disabled={sharing}
        >
          <WhatsAppIcon />
          {sharing ? "Preparing image…" : "Share on WhatsApp"}
        </button>
      </div>
    </Modal>
  );
}

export default TimetableModal;
